import React, { useState } from 'react';
import { Eye, EyeOff } from 'lucide-react';

export interface InputProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'size'> {
  error?: boolean;
  errorMessage?: string;
  label?: string;
  fullWidth?: boolean;
  inputSize?: 'sm' | 'md' | 'lg';
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
  showPasswordToggle?: boolean;
}

export const Input = React.forwardRef<HTMLInputElement, InputProps>(
  (
    {
      error = false,
      errorMessage,
      label,
      fullWidth = false,
      inputSize = 'md',
      leftIcon,
      rightIcon,
      showPasswordToggle = false,
      className = '',
      id,
      type,
      ...props
    },
    ref
  ) => {
    const [showPassword, setShowPassword] = useState(false);
    const inputId = id || label?.toLowerCase().replace(/\s+/g, '-');

    const isPasswordType = type === 'password';
    const effectiveType = isPasswordType && showPassword ? 'text' : type;

    const baseStyles = 'text-sm rounded-xl border transition-all duration-200 focus:outline-none bg-neutral-50/50 text-neutral-900 placeholder:text-neutral-400';

    const sizeStyles = {
      sm: 'h-9 text-xs',
      md: 'h-10 text-sm',
      lg: 'h-11 sm:h-[46px] text-sm',
    };

    const stateStyles = error
      ? 'border-red-500 focus:border-red-500 focus:ring-4 focus:ring-red-500/10'
      : 'border-neutral-200 focus:bg-white focus:border-neutral-300 focus:ring-4 focus:ring-primary-900/5';

    const disabledStyles = 'disabled:opacity-60 disabled:cursor-not-allowed';

    const widthStyles = fullWidth ? 'w-full' : '';

    const paddingLeft = leftIcon ? 'pl-10' : 'pl-4';
    const paddingRight = (showPasswordToggle && isPasswordType) || rightIcon ? 'pr-11' : 'pr-4';

    const combinedClassName = `${baseStyles} ${sizeStyles[inputSize]} ${stateStyles} ${disabledStyles} ${widthStyles} ${paddingLeft} ${paddingRight} ${className}`.trim();

    return (
      <div className={fullWidth ? 'w-full' : ''}>
        {label && (
          <label
            htmlFor={inputId}
            className="block text-[13px] font-medium text-neutral-700 mb-1.5"
          >
            {label}
          </label>
        )}
        <div className="relative group">
          {leftIcon && (
            <span className="absolute left-3.5 top-1/2 -translate-y-1/2 text-neutral-400 transition-colors duration-200 group-focus-within:text-neutral-600 pointer-events-none">
              {leftIcon}
            </span>
          )}
          <input
            ref={ref}
            id={inputId}
            type={effectiveType}
            className={combinedClassName}
            {...props}
          />
          {showPasswordToggle && isPasswordType && (
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-3 top-1/2 -translate-y-1/2 p-1.5 text-neutral-400 hover:text-neutral-600 transition-colors duration-200 rounded-lg hover:bg-neutral-100"
              tabIndex={-1}
              aria-label={showPassword ? 'Hide password' : 'Show password'}
            >
              {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            </button>
          )}
          {rightIcon && !showPasswordToggle && (
            <span className="absolute right-3.5 top-1/2 -translate-y-1/2 text-neutral-400">
              {rightIcon}
            </span>
          )}
        </div>
        {error && errorMessage && (
          <p className="mt-1.5 text-xs text-red-600">{errorMessage}</p>
        )}
      </div>
    );
  }
);

Input.displayName = 'Input';
